import Button from '../../../components/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SIGNALR_LISTENERS } from '../../../config/signalr';
import { Wrapper } from '../../Dashboard/style';
import { ProjectContainer } from './style';
import {
  useExcludeProject,
  useGetGroupedWorkflows,
  useGetWorkflowById,
  useUpdateWorkflow
} from '../../../services/workflow.services';
import Table from '../../../components/Table';
import deleteSheetImage from '../../../assets/images/delete-quarter.png';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DeleteConfirmModal } from '../../../components/DeleteConfirmModal';
import {
  useCreateAIFMPeriod,
  useCreatePeriod,
  useCreatePeriodAutomatic,
  useDeletePeriod,
  useUpdatePeriod
} from '../../../services/period.service';
import { IoClose } from 'react-icons/io5';
import toast from 'react-hot-toast';
import { TbWindmill } from 'react-icons/tb';
import { NewAIFMDrawer } from './components/NewAIFMDrawer';
import { ReadonlyAIFDrawer } from './components/ReadonlyAIFDrawer';
import { ActionLabel, CALCULATED_CELL_WIDTH, isFromScratch } from './data';
import { ReadonlyFilesDrawer } from './components/ReadonlyFilesDrawer';
import { ReadonlyJurisdictionDrawer } from './components/ReadonlyJurisdictionDrawer';
import { AIFColumn } from './components/Columns/AIFColumn';
import { JurisdictionColumn } from './components/Columns/JurisdictionColumn';
import { FilesColumn } from './components/Columns/FilesColumn';
import { ExtractionColumn } from './components/Columns/ExtractionColumn';
import { TransformationColumn } from './components/Columns/TransformationColumn';
import { ReportColumn } from './components/Columns/ReportColumn';
import { ReadonlyReportJurisdictionDrawer } from './components/ReadonlyReportJurisdictionDrawer';
import DeletePeriodModal from './components/DeletePeriodModal';
import RowAction from './components/RowAction';
import { WarningPopup } from '../../../components/WarningPopup';
import { ExtractionIssuesModal } from './components/ExtractionIssuesModal';
import { AddAIFM } from './AddAIFM';
import { Tabs } from './Tabs';
import { TbPlus } from 'react-icons/tb';
import { GenerateAIFMReportDrawer } from './components/GenerateAIFMReportDrawer';
import { useCreateAIFMReport, useRegenerateAIFMReport } from '../../../services/report.service';
import { TbPencil } from 'react-icons/tb';
import { TbShare } from 'react-icons/tb';
import { ShareAIFM } from './ShareAIFM';
import { NewDeleteConfirmModal } from '../../../components/NewDeleteConfirmModal';
import { MergeAIFM } from '../components/MergeAIFM';
import { AIFMReportColumn } from './components/Columns/AIFMReportColumn';
import { AIFMJurisdictionColumn } from './components/Columns/AIFMJurisdictionColumn';
import { TransformationIssuesModal } from './components/TransformationIssuesModal';
import { LuInfo } from 'react-icons/lu';
import { TbReload } from 'react-icons/tb';
import { ApprovalColumn } from './components/Columns/ApprovalColumn';
import { ApproverDrawer } from './ApproverDrawer';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { checkIsLockedAutomation } from '../../../utils/lockAutomation';
import { FaArrowRight } from 'react-icons/fa6';
import { SubmissionColumn } from './components/Columns/SubmissionColumn';
import agentStore from '../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../components/ChatWidget/constants';
import { InitialSetupExtractionModal } from './components/InitialSetupExtractionModal';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../consts/reportTypes';
import { Box } from '@mantine/core';
import moment from 'moment';
import { PermissionGuard } from '../../../components/PermissionGuard';
import { permissionPolicies } from '../../../permissions/policies';
import { extractApiError } from '../../../utils';
import { useWorkflowChrome } from '../../../components/Layout/WorkflowChromeContext';
import { useSignalRListener } from '../../../hooks/useSignalR';
import CloudImportModal from '../components/CloudImportModal';
import useCloudImportFlow from '../components/useCloudImportFlow';

function findNextPeriod(latestPeriod, latestYear) {
  if (!latestPeriod || !latestYear)
    return {
      nextPeriod: 'Q1',
      nextPeriodYear: '2026'
    };

  let nextPeriod, nextPeriodYear;

  if (latestPeriod.startsWith('Q')) {
    if (parseInt(latestPeriod[1]) === 4) {
      nextPeriod = 'Q1';
      nextPeriodYear = latestYear + 1;
    } else {
      nextPeriod = `Q${parseInt(latestPeriod[1]) + 1}`;
      nextPeriodYear = latestYear;
    }
  } else if (latestPeriod[0] === 'H') {
    if (latestPeriod[1] === '1') {
      nextPeriod = `${latestPeriod[0]}2`;
      nextPeriodYear = latestYear;
    } else {
      nextPeriod = `${latestPeriod[0]}1`;
      nextPeriodYear = latestYear + 1;
    }
  } else if (latestPeriod[0] === 'X') {
    if (latestPeriod[1] === '1') {
      nextPeriod = 'Q4';
      nextPeriodYear = latestYear;
    } else {
      nextPeriod = 'Q1';
      nextPeriodYear = latestYear + 1;
    }
  } else if (latestPeriod.startsWith('Y')) {
    nextPeriod = 'Y1';
    nextPeriodYear = latestYear + 1;
  }

  return { nextPeriod, nextPeriodYear };
}

export default function Quarters() {
  const { id } = useParams();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [selectedWorkflow, setSelectedWorkflow] = useState({});
  const isGrouped = searchParams.get('type') === 'grouped';
  const updateWorkflow = useUpdateWorkflow();

  const {
    data: workflowPeriods,
    isLoading,
    isFetching,
    refetch: refetchPeriods
  } = useGetWorkflowById({
    id,
    queryParams: {
      enabled: !!id && !isGrouped,
      // SignalR drives the live updates; this is a long-interval safety net
      // for missed messages (reconnects, etc.).
      refetchInterval: 30000
    }
  });

  const projectNamingByReportType = REPORT_NAMINGS.PROJECT?.[workflowPeriods?.report_type] || '';
  const fundNamingByReportType = REPORT_NAMINGS.FUND?.[workflowPeriods?.report_type] || '';

  const isReportTable = selectedWorkflow?.name === `${projectNamingByReportType} Report`;
  const excludeProject = useExcludeProject();
  const regenerateAIFMReport = useRegenerateAIFMReport();
  const {
    data: groupedWorkflows,
    isLoading: isLoadingGroups,
    refetch: refetchGroups
  } = useGetGroupedWorkflows({
    id,
    queryParams: {
      enabled: !!id && isGrouped,
      onSuccess: res => {
        if (!selectedWorkflow.id) setSelectedWorkflow(res.workflows[0]);
      },
      cacheTime: 0,
      staleTime: 0
    }
  });

  const refetch = isGrouped ? refetchGroups : refetchPeriods;

  const matchPeriodInThisWorkflow = useCallback(
    payload => {
      if (!payload) return false;
      // WorkflowPeriodUpdate carries workflow_id; status events carry period_id.
      if (payload.workflow_id && payload.workflow_id !== id) return false;
      if (payload.period_id) {
        const periodIds = (workflowPeriods?.periods ?? []).map(p => p.id);
        if (periodIds.length && !periodIds.includes(payload.period_id)) return false;
      }
      return true;
    },
    [id, workflowPeriods]
  );

  const handleSignal = useCallback(
    payload => {
      if (matchPeriodInThisWorkflow(payload)) refetch();
    },
    [matchPeriodInThisWorkflow, refetch]
  );

  useSignalRListener(SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, handleSignal);
  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED, handleSignal);

  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  const quarters = useMemo(() => {
    if (isGrouped) {
      if (!groupedWorkflows) return {};
      if (selectedWorkflow?.name === `${projectNamingByReportType} Report`) {
        return {
          ...groupedWorkflows,
          name: `${projectNamingByReportType} Report`
        };
      }
      return groupedWorkflows.workflows.find(item => item.id === selectedWorkflow.id);
    }
    return workflowPeriods;
  }, [workflowPeriods, selectedWorkflow, groupedWorkflows]);

  const reportTypeForDrawer = isGrouped
    ? groupedWorkflows?.workflows?.[0]?.report_type
    : workflowPeriods?.report_type;

  const canManageWorkflow = permissionPolicies.checkAccess({
    currentUserRole: currentUser?.role,
    currentUserId: currentUser?.azure_object_id,
    assignedTo:
      quarters?.assigned_to || groupedWorkflows?.assigned_to || workflowPeriods?.assigned_to
  });

  const createInitialPeriod = useCreatePeriod();
  const createPeriod = useCreatePeriodAutomatic();
  const createAIFMPeriod = useCreateAIFMPeriod();
  const createAIFMReport = useCreateAIFMReport();
  const updatePeriod = useUpdatePeriod();
  const deletePeriod = useDeletePeriod();

  const [action, setAction] = useState();
  const [state, setState] = useState({});
  const [quarter, setQuarter] = useState();
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [deletePeriodId, setDeletePeriodId] = useState(null);

  const [allowFilesToGetEdited, setAllowFilesToGetEdited] = useState(false);

  const [isOpenCreatePeriodPopup, setIsOpenCreatePeriodPopup] = useState(false);
  const [isOpenNewPeriodDrawer, setIsOpenNewPeriodDrawer] = useState(false);
  const [isOpenReadonlyAIFDrawer, setIsOpenReadonlyAIFDrawer] = useState(false);
  const [isOpenReadonlyFilesDrawer, setIsOpenReadonlyFilesDrawer] = useState(false);
  const [isOpenReadonlyJurisdictionDrawer, setIsOpenReadonlyJurisdictionDrawer] = useState(false);
  const [isOpenExtractionIssuesDrawer, setIsOpenExtractionIssuesDrawer] = useState(false);
  const [isOpenInitialExtractionDrawer, setIsOpenInitialExtractionDrawer] = useState(false);
  const [isOpenReportJurisdictionDrawer, setIsOpenReportJurisdictionDrawer] = useState(false);
  const [isOpenTransformationDrawer, setIsOpenTransformationDrawer] = useState(false);
  const [isOpenAddAIFM, setIsOpenAddAIFM] = useState(false);
  const [isOpenEditAIFM, setIsOpenEditAIFM] = useState(false);
  const [isOpenShareAIFM, setIsOpenShareAIFM] = useState(false);
  const [isOpenGenerateReport, setIsOpenGenerateReport] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenApprover, setIsOpenApprover] = useState(null);
  const [isOpenUpdateAIFsModal, setIsOpenUpdateAIFsModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedCloudImportPeriodId, setSelectedCloudImportPeriodId] = useState(null);

  const isConversionReportType = workflowPeriods?.report_type === REPORT_TYPES.CONVERSION;
  const isCustomReportType = workflowPeriods?.report_type === REPORT_TYPES.CUSTOM;
  const { cloudImportModalProps, isCloudIntegrationEnabled, openCloudModal } = useCloudImportFlow({
    onImported: refetch,
    periodId: selectedCloudImportPeriodId,
    reportType: quarters?.report_type
  });

  const handleOpenCloudImport = useCallback(
    periodId => {
      setSelectedCloudImportPeriodId(periodId);
      openCloudModal();
    },
    [openCloudModal]
  );

  const handleOpenExtractionIssues = periodId => {
    setSelectedPeriodId(periodId);
    setIsOpenExtractionIssuesDrawer(true);
  };

  const handleOpenInitialExtraction = periodId => {
    setSelectedPeriodId(periodId);
    setIsOpenInitialExtractionDrawer(true);
  };

  const handleOpenTransformationIssues = periodId => {
    setSelectedPeriodId(periodId);
    setIsOpenTransformationDrawer(true);
  };

  const handleOpenTransformationDrawer = el => {
    setSelectedPeriod(el);
    handleOpenTransformationIssues(el?.id);
  };

  const handleCloseTransformationDrawer = () => {
    setIsOpenTransformationDrawer(false);
    setSelectedPeriod(null);
  };

  const handleDeletePeriod = () => {
    deletePeriod.mutate(deletePeriodId, {
      onSuccess: () => {
        refetch();
        setDeletePeriodId(null);
        toast.success('Reporting Period has been deleted');
      },
      onError: err => {
        toast.error(`Failed to delete period. ${extractApiError(err)}`);
      }
    });
  };

  const handleOpenApprover = el => {
    setIsOpenApprover(el.id);
    setSelectedPeriodId(el.id);
  };

  const onExcludeProject = onSuccess => {
    if (!selectedWorkflow) return toast.error('No Project has been selected');

    excludeProject.mutate(
      {
        workflow_id: selectedWorkflow?.id
      },
      {
        onSuccess: () => {
          onSuccess();
          setSelectedWorkflow({});
          setIsOpenDeleteModal(false);
          toast.success('Project has been excluded');
          refetch();
        }
      }
    );
  };

  const aifmColumns = [
    {
      title: 'Year',
      key: 'year',
      sort: true,
      tdProps: {
        className: 'td-item',
        onClick: record => {
          setQuarter(record);
          setIsOpenNewPeriodDrawer(true);
        }
      }
    },
    {
      title: 'Period',
      key: 'period',
      sort: true,
      tdProps: {
        className: 'td-item',
        onClick: record => {
          setQuarter(record);
          setIsOpenNewPeriodDrawer(true);
        }
      }
    },

    {
      // Match the per-workflow report_type so Form PF projects label this
      // column "Fund Info" instead of the default "Jurisdictions".
      title: REPORT_NAMINGS.JURISDICTION[quarters?.report_type] || 'Jurisdictions',
      key: 'jurisdictions',

      tdProps: {
        className: 'td-item'
      },
      render: el => <AIFMJurisdictionColumn period={el} />
    },
    {
      title: 'Report Generation',
      key: 'report',
      tdProps: {
        className: 'td-item'
      },
      render: el => <AIFMReportColumn period={el} workflowId={id} />
    },
    {
      title: 'Approval',
      key: 'approval',
      tdProps: {
        className: 'td-item'
      },
      render: () => '-'
    },
    {
      title: 'Submission',
      key: 'submmission',
      thStyle: { borderColor: 'transparent' },
      tdProps: {
        className: 'td-item',
        style: {
          borderColor: 'transparent'
        }
      },
      render: () => '-'
    },
    {
      title: '',
      key: 'action',
      tdProps: {
        style: {
          width: 20
        }
      },
      render: el => {
        return <RowAction onDelete={() => setDeletePeriodId(el.id)} />;
      }
    }
  ];

  const checkIsLastPeriod = periodId => quarters?.periods?.[0]?.id === periodId;

  let columns = [
    {
      title: 'Period',
      key: 'period',
      sort: true,
      tdProps: {
        style: {
          width: 120,
          maxWidth: 120,
          minWidth: 120
        },
        className: 'td-item',
        onClick: record => {
          const isPeriodLatest = record?.id === quarters?.periods?.[0]?.id;
          const isLocked = checkIsLockedAutomation(record, currentUser);

          if (isLocked || !isPeriodLatest) return;
          setQuarter(record);
          setIsOpenNewPeriodDrawer(true);
        }
      },
      render: el => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ActionLabel label={`${el.period},${el.year}`} rightIcon={<FaArrowRight />} />
            {el.filing_type && (
              <span style={{ fontSize: 11, color: '#868e96' }}>
                {el.filing_type.replace(/_/g, '-')}
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: `${fundNamingByReportType || REPORT_NAMINGS.FUND[REPORT_TYPES.ANNEX_IV]}s`,
      key: 'aif',
      tdProps: {
        className: 'td-item',
        style: {
          height: 74,
          width: 120,
          maxWidth: 120,
          minWidth: 120
        }
      },
      render: el => (
        <AIFColumn
          period={el}
          refetch={refetch}
          reportType={quarters?.report_type}
          setSelectedPeriodId={setSelectedPeriodId}
          setIsOpenReadonlyAIFDrawer={setIsOpenReadonlyAIFDrawer}
          setIsOpenUpdateAIFsModal={setIsOpenUpdateAIFsModal}
        />
      )
    },
    {
      title: `${REPORT_NAMINGS.JURISDICTION[quarters?.report_type] || 'Jurisdictions'}`,
      key: 'jurisdictions',

      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <JurisdictionColumn
          period={el}
          refetch={refetch}
          reportType={quarters?.report_type}
          setSelectedPeriodId={setSelectedPeriodId}
          setIsOpenReadonlyJurisdictionDrawer={setIsOpenReadonlyJurisdictionDrawer}
        />
      )
    },
    {
      title: 'Source Files',
      key: 'file_count',
      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <FilesColumn
          workflowId={id}
          period={el}
          refetch={refetch}
          reportType={quarters?.report_type}
          canManageWorkflow={canManageWorkflow}
          isCloudIntegrationEnabled={isCloudIntegrationEnabled}
          onImportFromIntegration={handleOpenCloudImport}
          setAllowFilesToGetEdited={setAllowFilesToGetEdited}
          setSelectedPeriodId={setSelectedPeriodId}
          setIsOpenReadonlyFilesDrawer={setIsOpenReadonlyFilesDrawer}
        />
      )
    },
    {
      title: 'Extraction',
      key: 'extraction',
      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <ExtractionColumn
          period={el}
          refetch={refetch}
          workflowId={isGrouped ? selectedWorkflow.id : id}
          reportType={quarters?.report_type}
          canManageWorkflow={canManageWorkflow}
          isLoadingPeriods={isFetching}
          onOpenDrawer={handleOpenExtractionIssues}
          onOpenInitialExtractionDrawer={handleOpenInitialExtraction}
        />
      )
    },
    {
      title: 'Transformation',
      key: 'transformation',
      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <TransformationColumn
          period={el}
          reportType={quarters?.report_type}
          workflowId={isGrouped ? selectedWorkflow.id : id}
          refetch={refetch}
          canManageWorkflow={canManageWorkflow}
          isLoadingPeriods={isFetching}
          openDrawer={() => handleOpenTransformationDrawer(el)}
        />
      )
    },
    {
      title: quarters?.report_type === REPORT_TYPES.CUSTOM ? 'Output Table' : 'Report Generation',
      key: 'report',
      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <ReportColumn
          period={el}
          refetch={refetch}
          reportType={quarters?.report_type}
          isLoadingPeriods={isFetching}
          workflowId={isGrouped ? selectedWorkflow.id : id}
          setSelectedPeriod={setSelectedPeriodId}
          setIsOpenReportJurisdiction={setIsOpenReportJurisdictionDrawer}
        />
      )
    },
    {
      title: 'Approval/Comments',
      key: 'approval',
      tdProps: {
        className: 'td-item',
        style: {
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => (
        <ApprovalColumn
          period={el}
          reportType={quarters?.report_type}
          workflowId={id}
          canManageWorkflow={canManageWorkflow}
          onOpenApprover={() => handleOpenApprover(el)}
        />
      )
    },
    {
      title: 'Submission',
      key: 'submmission',
      thStyle: { borderColor: 'transparent' },
      tdProps: {
        className: 'td-item',
        style: {
          borderColor: 'transparent',
          width: CALCULATED_CELL_WIDTH,
          maxWidth: CALCULATED_CELL_WIDTH,
          minWidth: CALCULATED_CELL_WIDTH
        }
      },
      render: el => {
        return (
          <SubmissionColumn period={el} refetch={refetch} reportType={quarters?.report_type} />
        );
      }
    },
    {
      title: '',
      key: 'action',
      tdProps: {
        style: {
          width: 40,
          maxWidth: 40
        }
      },
      render: el => {
        const isLocked = checkIsLockedAutomation(el, currentUser);
        const isScratchPeriod = isFromScratch(el);
        const isLastPeriod = checkIsLastPeriod(el?.id);

        if (isLocked || !isLastPeriod || !canManageWorkflow)
          return <Box sx={{ width: 50, maxWidth: 50 }} />;

        return <RowAction onDelete={() => setDeletePeriodId(el.id)} />;
      }
    }
  ];

  if (isConversionReportType) {
    const aifColumn = { ...columns?.[1] };
    const jurisdictionColumn = { ...columns?.[2] };
    const filesColumn = { ...columns?.[3] };

    columns = columns.filter(col => col.key !== 'extraction' && col.key !== 'transformation');

    columns[1] = { ...filesColumn };
    columns[2] = { ...aifColumn };
    columns[3] = { ...jurisdictionColumn };
  }

  if (isCustomReportType) {
    columns = columns.filter(
      col => !['aif', 'jurisdictions', 'approval', 'submmission'].includes(col.key)
    );
  }

  const onAction = (type, item) => {
    setAction(type);
    setQuarter(item);
  };

  const onConfirmDelete = () => {
    deletePeriod.mutate(quarter.id, {
      onSuccess: () => {
        onAction();
        refetch();
      }
    });
  };

  const handleCreateAIFMPeriod = value => {
    createAIFMPeriod.mutate(
      {
        id: quarters?.id,
        body: {
          year: +value.year,
          period: value.period
        }
      },
      {
        onSuccess: res => {
          createAIFMReport.mutate(
            { id: res.id },
            {
              onSuccess: () => {
                refetch();
                onClear();
                toast.success(
                  `Multi-${REPORT_NAMINGS.PROJECT[quarters?.report_type]} Report has been generated`,
                  {
                    position: 'bottom-center'
                  }
                );
              },
              onError: res => {
                toast.error(res.data?.detail);
              }
            }
          );
        },
        onError: res => {
          toast.error(res.data?.detail);
        }
      }
    );
  };

  const handleCreatePeriod = vals => {
    const hasInitialPeriod = quarters?.periods?.some(period => isFromScratch(period));

    if (hasInitialPeriod) {
      createPeriod.mutate(
        {
          year: +state.year,
          period: state.period,
          workflow_id: isGrouped ? selectedWorkflow?.id : quarters?.id
        },
        {
          onSuccess: () => {
            refetch();
            onClear();

            toast.success('New Reporting Period has been created', {
              position: 'bottom-center'
            });
          },
          onError: err => {
            toast.error(`Failed to create period. ${extractApiError(err)}`);
          }
        }
      );
    } else {
      createInitialPeriod.mutate(
        {
          year: +state.year,
          period: state.period,
          workflow_id: isGrouped ? selectedWorkflow?.id : quarters?.id
        },
        {
          onSuccess: () => {
            refetch();
            onClear();
            toast.success('New Reporting Period has been created', {
              position: 'bottom-center'
            });
          },
          onError: err => {
            toast.error(`Failed to create period. ${extractApiError(err)}`);
          }
        }
      );
    }
  };

  const onSubmitNewPeriod = vals => {
    handleCreatePeriod(vals);
  };

  const onSubmitUpdateAIF = () => {
    navigate(`${selectedPeriodId}/add-aifs?with_jurisdiction=true`);
  };

  const onCloseUpdateAIFModal = () => {
    setIsOpenUpdateAIFsModal(false);
    setSelectedPeriodId(null);
  };

  const onClear = () => {
    setIsOpenNewPeriodDrawer(false);
    setIsOpenCreatePeriodPopup(false);
    setIsOpenGenerateReport(false);
    setState({});
  };

  const defaultValues = useMemo(() => {
    if (!quarters) return {};

    if (quarter) {
      return {
        id: quarter.id,
        year: `${quarter.year}`,
        projectName: quarters?.name,
        period: quarter.period,
        endDate: quarter.end_date,
        filing_type: quarter.filing_type,
        filing_time: quarter.filing_time,
        version: quarter.version
      };
    }

    const { nextPeriod, nextPeriodYear } = findNextPeriod(
      quarters.periods?.[0]?.period,
      quarters.periods?.[0]?.year
    );

    return {
      year: `${nextPeriodYear}`,
      projectName: quarters?.name,
      period: nextPeriod
    };
  }, [quarters, quarter]);

  const onCloseAIFMDrawer = () => {
    setIsOpenNewPeriodDrawer(false);
    setQuarter();
  };

  const onSubmitNewAIFM = values => {
    const workflowId = quarters?.id;
    const isNameChanged = values?.name && values.name !== quarters?.name;

    const editOrCreate = () => {
      if (quarter) {
        updatePeriod.mutate(
          {
            periodId: quarter.id,
            data: values
          },
          {
            onSuccess: () => {
              refetch();
              setIsOpenNewPeriodDrawer(false);
              setQuarter();
            },
            onError: err => {
              toast.error(`Failed to update period. ${extractApiError(err)}`);
            }
          }
        );
      } else {
        setIsOpenNewPeriodDrawer(false);
        setIsOpenCreatePeriodPopup(true);
        setState(values);
      }
    };

    if (isNameChanged && workflowId) {
      updateWorkflow.mutate(
        {
          id: workflowId,
          data: { name: values.name }
        },
        {
          onSuccess: () => {
            refetch();
            editOrCreate();
          },
          onError: err => {
            toast.error(extractApiError(err, 'Failed to update name'));
          }
        }
      );
      return;
    }

    editOrCreate();
  };

  const toggleAIFM = () => {
    setIsOpenAddAIFM(prev => !prev);
  };

  const onCloseShareAIFM = () => {
    setIsOpenShareAIFM(false);
  };

  const onCloseEditAIFM = () => {
    setIsOpenEditAIFM(false);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const onCloseGenerateReport = () => {
    setIsOpenGenerateReport(false);
  };

  const onCloseApprover = () => {
    setIsOpenApprover(null);
  };

  const tabs = useMemo(() => {
    if (groupedWorkflows) {
      return [
        {
          ...groupedWorkflows,
          name: `${projectNamingByReportType} Report`,
          count: groupedWorkflows.periods.length
        },
        ...groupedWorkflows.workflows
      ];
    }
    return [];
  }, [groupedWorkflows]);

  const isUpdateReport =
    groupedWorkflows?.periods?.[groupedWorkflows?.periods?.length - 1]?.is_updated;

  const onRegenerateAIFMReport = () => {
    toast.promise(
      regenerateAIFMReport.mutateAsync(
        {
          periodId: groupedWorkflows?.periods?.[groupedWorkflows?.periods?.length - 1].id
        },
        {
          onSuccess: () => {
            refetch();
          }
        }
      ),
      {
        loading: 'Generating report',
        success: `Consolidated ${REPORT_NAMINGS.PROJECT[quarters?.report_type]} Report has been generated`,
        error: 'Something was wrong while generating report'
      }
    );
  };

  const isNewAIFMDrawerLoading =
    createPeriod.isLoading ||
    createInitialPeriod.isLoading ||
    updatePeriod.isLoading ||
    updateWorkflow.isLoading;

  useEffect(() => {
    agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.Dashboard);
  }, []);

  const { setLabels, clearLabels } = useWorkflowChrome();
  useEffect(() => {
    // Only project chip on the Quarters page — periods are listed below, not selected.
    setLabels({ projectLabel: workflowPeriods?.name });
    return () => clearLabels();
  }, [workflowPeriods?.name, setLabels, clearLabels]);

  return (
    <>
      <Wrapper>
        <ProjectContainer>
          <div className="header">
            {isGrouped && (
              <div className="buttons">
                <Button
                  onClick={() => setIsOpenShareAIFM(true)}
                  size="sm"
                  variant="unstyled"
                  leftIcon={<TbShare fontSize="14px" />}
                  disabled
                >
                  Share
                </Button>
                <PermissionGuard allowed={canManageWorkflow}>
                  <Button
                    size="sm"
                    variant="unstyled"
                    leftIcon={<TbPlus fontSize="14px" />}
                    onClick={() => setIsOpenAddAIFM(true)}
                  >
                    Add More{' '}
                    {REPORT_NAMINGS.PROJECT[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s
                  </Button>
                </PermissionGuard>
                <PermissionGuard allowed={canManageWorkflow}>
                  <Button
                    size="sm"
                    variant="unstyled"
                    leftIcon={<TbPencil fontSize="14px" />}
                    onClick={() => setIsOpenEditAIFM(true)}
                  >
                    Edit Consolidated{' '}
                    {REPORT_NAMINGS.PROJECT[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}
                  </Button>
                </PermissionGuard>
              </div>
            )}
          </div>
          <div className="box">
            {isGrouped && (
              <div className="actions-bar">
                <div className="actions-bar__left">
                  <Tabs
                    tabs={tabs}
                    onChange={value => setSelectedWorkflow(value)}
                    value={selectedWorkflow}
                  />
                </div>
                <div className="actions-bar__right"></div>
              </div>
            )}
            <div className="actions-bar">
              <div className="actions-bar__left">
                {isReportTable && (
                  <>
                    {isUpdateReport ? (
                      <>
                        <div className="warning-report">
                          <LuInfo fontSize="16px" />
                          There was a change in your projects list, please regenerate the reports
                        </div>
                        <PermissionGuard allowed={canManageWorkflow}>
                          <Button
                            onClick={onRegenerateAIFMReport}
                            disabled={regenerateAIFMReport.isLoading}
                            size="sm"
                            variant="outlined"
                            leftIcon={<TbReload fontSize="14px" />}
                          >
                            Re-generate Consolidated{' '}
                            {REPORT_NAMINGS.PROJECT[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}{' '}
                            Report
                          </Button>
                        </PermissionGuard>
                      </>
                    ) : (
                      <PermissionGuard allowed={canManageWorkflow}>
                        <Button
                          size="sm"
                          variant="outlined"
                          leftIcon={<TbWindmill fontSize="14px" />}
                          disabled={createPeriod.isLoading || createInitialPeriod.isLoading}
                          onClick={() => setIsOpenGenerateReport(true)}
                        >
                          Generate Consolidated{' '}
                          {REPORT_NAMINGS.PROJECT[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}{' '}
                          Report
                        </Button>
                      </PermissionGuard>
                    )}
                  </>
                )}
                {!isReportTable && (
                  <PermissionGuard allowed={canManageWorkflow}>
                    <Button
                      size="sm"
                      variant="outlined"
                      leftIcon={<TbPlus fontSize="14px" />}
                      disabled={
                        createPeriod.isLoading ||
                        createInitialPeriod.isLoading ||
                        quarters?.periods?.[0]?.filing_type === 'PF_FINAL'
                      }
                      onClick={() => setIsOpenNewPeriodDrawer(true)}
                    >
                      Create New Period
                    </Button>
                  </PermissionGuard>
                )}
              </div>
              <div className="actions-bar__right">
                {isGrouped && !isReportTable && (
                  <PermissionGuard allowed={canManageWorkflow}>
                    <Button
                      size="sm"
                      variant="unstyled-gray"
                      disabled={!selectedWorkflow}
                      leftIcon={<IoClose fontSize="14px" />}
                      onClick={() => setIsOpenDeleteModal(true)}
                    >
                      Exclude this project
                    </Button>
                  </PermissionGuard>
                )}
              </div>
            </div>

            <Table
              hasIndex={false}
              isLoading={isLoading || isLoadingGroups}
              columns={isReportTable ? aifmColumns : columns}
              data={quarters?.periods || []}
              onRowClick={() => {}}
            />
          </div>
        </ProjectContainer>
      </Wrapper>

      <NewAIFMDrawer
        key={quarter?.id ?? 'new'}
        isOpen={isOpenNewPeriodDrawer}
        onClose={onCloseAIFMDrawer}
        onSubmit={onSubmitNewAIFM}
        refetch={refetch}
        isNameReadonly={false}
        isLoading={isNewAIFMDrawerLoading}
        defaultValues={defaultValues}
        isEdit={!!quarter}
        reportType={reportTypeForDrawer}
        workflowId={isGrouped ? selectedWorkflow?.id : id}
        isAutomation={true}
      />
      <GenerateAIFMReportDrawer
        isOpen={isOpenGenerateReport}
        onClose={onCloseGenerateReport}
        onSubmit={handleCreateAIFMPeriod}
        isLoading={createAIFMPeriod.isLoading || createAIFMReport.isLoading}
        defaultValues={defaultValues}
      />

      <DeleteConfirmModal
        onClose={onAction}
        image={deleteSheetImage}
        isOpen={action === 'delete'}
        onConfirm={onConfirmDelete}
        isLoading={deletePeriod.isLoading}
        title={`Are you sure you want to permanently delete 'Quarter-${quarter?.quarter}, ${quarter?.year}' reporting period?`}
        description="All relevant information will be deleted including data <br/>extractions and reports"
      />

      <ExtractionIssuesModal
        worklowId={id}
        isOpen={isOpenExtractionIssuesDrawer}
        periodId={selectedPeriodId}
        refetch={refetch}
        currentUser={currentUser}
        assignedTo={quarters?.assigned_to}
        canManageWorkflow={canManageWorkflow}
        isLatestPeriod={
          selectedPeriodId === quarters?.periods?.[0]?.id || currentUser?.role === 'Admin'
        }
        onClose={() => setIsOpenExtractionIssuesDrawer(false)}
      />

      <InitialSetupExtractionModal
        worklowId={id}
        isOpen={isOpenInitialExtractionDrawer}
        onClose={() => setIsOpenInitialExtractionDrawer(false)}
        periodId={selectedPeriodId}
        refetch={refetch}
      />

      <TransformationIssuesModal
        worklowId={id}
        isOpen={isOpenTransformationDrawer}
        periodId={selectedPeriodId}
        period={selectedPeriod}
        refetch={refetch}
        isLatestPeriod={
          selectedPeriodId === quarters?.periods?.[0]?.id || currentUser?.role === 'Admin'
        }
        onClose={handleCloseTransformationDrawer}
      />

      <ReadonlyAIFDrawer
        isOpen={isOpenReadonlyAIFDrawer}
        onClose={() => setIsOpenReadonlyAIFDrawer(false)}
        periodId={selectedPeriodId}
        selectedPeriod={quarters?.periods?.find(item => item.id === selectedPeriodId)}
        currentUser={currentUser}
        reportType={quarters?.report_type}
        isLatestPeriod={
          selectedPeriodId === quarters?.periods?.[0]?.id || currentUser?.role === 'Admin'
        }
      />

      <ReadonlyFilesDrawer
        isOpen={isOpenReadonlyFilesDrawer}
        workflowId={id}
        periodId={selectedPeriodId}
        allowEdit
        selectedPeriod={quarters?.periods?.find(item => item.id === selectedPeriodId)}
        onClose={() => setIsOpenReadonlyFilesDrawer(false)}
        currentUser={currentUser}
        isCustomReportType={isCustomReportType}
        isLatestPeriod={
          selectedPeriodId === quarters?.periods?.[0]?.id || currentUser?.role === 'Admin'
        }
      />

      <ReadonlyJurisdictionDrawer
        workflowId={id}
        periodId={selectedPeriodId}
        reportType={quarters?.report_type}
        isLatestPeriod={
          selectedPeriodId === quarters?.periods?.[0]?.id || currentUser?.role === 'Admin'
        }
        handleOpenJurisdictionDrawer={() => {
          setIsOpenReadonlyJurisdictionDrawer(false);
          const selectedPeriod = quarters?.periods?.find(item => item.id === selectedPeriodId);
          if (!selectedPeriod) return;
          const isAutomation =
            selectedPeriod.template_type && selectedPeriod.template_type !== 'sratch';
          navigate(
            isAutomation
              ? `/project/quaters/${id}/${selectedPeriodId}/jurisdictions`
              : `/project/${id}/jurisdictions?periodId=${selectedPeriodId}`
          );
        }}
        isOpen={isOpenReadonlyJurisdictionDrawer}
        selectedPeriod={quarters?.periods?.find(item => item.id === selectedPeriodId)}
        onClose={() => setIsOpenReadonlyJurisdictionDrawer(false)}
        currentUser={currentUser}
      />

      <ReadonlyReportJurisdictionDrawer
        workflowId={id}
        periodId={selectedPeriodId}
        isOpen={isOpenReportJurisdictionDrawer}
        onClose={() => setIsOpenReportJurisdictionDrawer(false)}
      />

      <DeletePeriodModal
        isOpen={!!deletePeriodId}
        onClose={() => setDeletePeriodId(null)}
        onDelete={handleDeletePeriod}
        isLoading={deletePeriod.isLoading}
      />

      <AddAIFM
        isOpen={isOpenAddAIFM}
        onClose={toggleAIFM}
        workflows={groupedWorkflows?.workflows || []}
        groupName={groupedWorkflows?.name}
        groupId={id}
        refetch={refetch}
      />

      <MergeAIFM
        defaultValues={{
          name: groupedWorkflows?.name,
          workflow_ids: groupedWorkflows?.workflows?.map(item => item.id)
        }}
        isEdit
        isOpen={isOpenEditAIFM}
        onClose={onCloseEditAIFM}
        refetch={refetch}
        groupId={id}
      />

      <ShareAIFM
        isOpen={isOpenShareAIFM}
        onClose={onCloseShareAIFM}
        workflows={groupedWorkflows?.workflows || []}
        groupName={groupedWorkflows?.name}
        groupId={id}
        refetch={refetch}
      />

      <NewDeleteConfirmModal
        title="Exclude Project"
        description="This project will be returned to the Project list and will remain available for use."
        isOpen={isOpenDeleteModal}
        onClose={onCloseDeleteModal}
        submitLabel="Yes, Exclude Project"
        cancelLabel="Cancel"
        type="exclude"
        onConfirm={onExcludeProject}
        isLoading={excludeProject.isLoading}
      />

      <WarningPopup
        isOpen={isOpenCreatePeriodPopup}
        onClose={() => setIsOpenCreatePeriodPopup(false)}
        onConfirm={onSubmitNewPeriod}
        isLoading={createPeriod.isLoading || createInitialPeriod.isLoading}
        title="Create New Period"
        description="Creating a new period will make the previous one unavailable for edits. Are you sure you want to proceed with this action?"
        confirmLabel="Yes, create new one"
      />

      <ApproverDrawer
        isOpen={!!isOpenApprover}
        periodId={isOpenApprover}
        onClose={onCloseApprover}
        currentUser={currentUser}
        selectedPeriod={quarters?.periods?.find(item => item.id === selectedPeriodId)}
      />

      <WarningPopup
        isOpen={isOpenUpdateAIFsModal}
        onClose={onCloseUpdateAIFModal}
        onConfirm={onSubmitUpdateAIF}
        title="Copying Funds from Previous Period"
        description={`${(isOpenUpdateAIFsModal && workflowPeriods?.periods?.find(el => el.id === selectedPeriodId)?.fund_count) || ' '} funds from your previous reporting period will be copied into the new one.<br/><br/>Would you like to add new funds or remove some of the copied ones?`}
        confirmLabel="Yes"
        cancelLabel="No"
      />

      {isCloudIntegrationEnabled ? (
        <CloudImportModal
          {...cloudImportModalProps}
          onClose={() => {
            cloudImportModalProps.onClose();
            setSelectedCloudImportPeriodId(null);
          }}
        />
      ) : null}
    </>
  );
}
